import { ref, onMounted } from 'vue';

export function usePWAInstall() {
  const deferredPrompt = ref<any>(null);
  const isInstallable = ref(false);
  const isInStandaloneMode = ref(false);
  const justInstalled = ref(false);

  // Función mejorada para detectar si estamos en modo PWA
  const checkStandaloneMode = (): boolean => {
    // Método 1: display-mode (más confiable)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // Método 2: navigator.standalone (iOS)
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    return false;
  };

  const updateInstallationStatus = () => {
    const standalone = checkStandaloneMode();
    isInStandaloneMode.value = standalone;
  };

  const showInstallPrompt = async (): Promise<boolean> => {
    if (!deferredPrompt.value) return false;

    try {
      deferredPrompt.value.prompt();
      const { outcome } = await deferredPrompt.value.userChoice;

      if (outcome === 'accepted') {
        deferredPrompt.value = null;
        isInstallable.value = false;

        // Marcar que se detectó una instalación
        (window as any).pwaInstallationDetected = true;

        console.log('Instalación aceptada, esperando detección de modo standalone...');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error durante la instalación:', error);
      return false;
    }
  };

  onMounted(() => {
    // Marcar tiempo de inicialización para heurística
    (window as any).pwaInitTime = Date.now();

    // Verificar estado inicial después de un pequeño delay para que los eventos se registren
    setTimeout(() => {
      updateInstallationStatus();
    }, 1000);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e;
      isInstallable.value = true;
    };

    const handleAppInstalled = () => {
      console.log('PWA fue instalada - evento appinstalled disparado');
      isInstallable.value = false;
      deferredPrompt.value = null;

      // Marcar como recién instalada para mostrar modal
      justInstalled.value = true;

      // Actualizar estado
      updateInstallationStatus();
    };

    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      isInStandaloneMode.value = e.matches;

      if (e.matches) {
        console.log('Cambió a modo standalone');
        isInstallable.value = false;
        deferredPrompt.value = null;
      } else {
        console.log('Cambió a modo navegador');
        isInStandaloneMode.value = false;
        // Verificar si está instalada pero en navegador
        updateInstallationStatus();
      }
    };

    const handleVisibilityChange = () => {
      // Actualizar estado cuando la pestaña se vuelve visible
      if (!document.hidden) {
        setTimeout(() => {
          updateInstallationStatus();
        }, 100);
      }
    };

    const handleFocus = () => {
      // Actualizar estado cuando la ventana recibe foco
      setTimeout(() => {
        updateInstallationStatus();
      }, 100);
    };

    // Agregar listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
    standaloneMediaQuery.addListener(handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      standaloneMediaQuery.removeListener(handleDisplayModeChange);
    };
  });

  return {
    isInstallable,
    justInstalled,
    isInStandaloneMode,
    showInstallPrompt,
    updateInstallationStatus
  };
}
